import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Trash2, X } from 'lucide-react';
import { outreachApi } from '../api/outreachApi';
import LeadStatusPicker from './LeadStatusPicker';

export default function LeadDetailPanel({ leadId, statuses, onClose, onChanged }) {
  const { t } = useTranslation('common');
  const [detail, setDetail] = useState(null);
  const [notes, setNotes] = useState('');
  const [error, setError] = useState(null);
  const [savingNotes, setSavingNotes] = useState(false);
  const [confirmingErase, setConfirmingErase] = useState(false);
  const [erasing, setErasing] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setDetail(null);
    setError(null);
    outreachApi
      .getLead(leadId)
      .then((data) => {
        if (cancelled) return;
        setDetail(data);
        setNotes(data.notes || '');
      })
      .catch((e) => !cancelled && setError(e?.response?.data?.detail || e.message));
    return () => {
      cancelled = true;
    };
  }, [leadId]);

  const applyStatus = async (payload) => {
    await outreachApi.setLeadStatus(leadId, payload);
    setDetail(await outreachApi.getLead(leadId));
    await onChanged();
  };

  const eraseLead = async () => {
    setErasing(true);
    try {
      await outreachApi.eraseLead(leadId);
      await onChanged();
      onClose();
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
      setErasing(false);
    }
  };

  const saveNotes = async () => {
    setSavingNotes(true);
    try {
      setDetail(await outreachApi.setLeadNotes(leadId, notes));
    } finally {
      setSavingNotes(false);
    }
  };

  return (
    <aside className="lead-detail">
      <div className="lead-detail-head">
        <strong>{detail ? detail.lead.email : t('outreach.loading')}</strong>
        <button className="unibox-page-button" onClick={onClose}>
          <X size={14} />
        </button>
      </div>

      {error && <div className="unibox-banner">{String(error)}</div>}

      {detail && (
        <>
          <div className="lead-detail-row">
            <span className="lead-detail-label">{t('outreach.leads.pipelineStatus')}</span>
            <LeadStatusPicker statuses={statuses} value={detail.lead.pipeline_status} onApply={applyStatus} />
          </div>
          <div className="lead-detail-row">
            <span className="lead-detail-label">{t('outreach.leads.engagementStatus')}</span>
            <span className="unibox-badge">
              {t(`outreach.leads.engagement.${detail.lead.engagement_status}`, detail.lead.engagement_status)}
            </span>
          </div>
          {detail.lead.follow_up_at && (
            <div className="lead-detail-row">
              <span className="lead-detail-label">{t('outreach.leads.followUpAt')}</span>
              <span>{new Date(detail.lead.follow_up_at).toLocaleDateString()}</span>
            </div>
          )}

          <h4 className="lead-detail-heading">{t('outreach.leads.campaignsHeading')}</h4>
          {detail.campaigns.length === 0 && <div className="unibox-empty">{t('outreach.leads.noCampaigns')}</div>}
          {detail.campaigns.map((c) => (
            <div key={c.campaign_id} className="lead-detail-row">
              <span>{c.campaign_name}</span>
              <span className="unibox-badge">
                {c.paused_until && !c.stop_reason
                  ? t('outreach.leads.heldUntil', { date: new Date(c.paused_until).toLocaleDateString() })
                  : t(`outreach.campaign.enrollment.${c.enrollment_status}`, c.enrollment_status)}
                {c.stop_reason ? ` · ${t(`outreach.campaign.stopReason.${c.stop_reason}`, c.stop_reason)}` : ''}
              </span>
            </div>
          ))}

          <h4 className="lead-detail-heading">{t('outreach.leads.notes')}</h4>
          <textarea className="unibox-reply-textarea" value={notes} onChange={(e) => setNotes(e.target.value)} />
          <button className="unibox-refresh" onClick={saveNotes} disabled={savingNotes}>
            {savingNotes ? t('outreach.leads.saving') : t('outreach.leads.saveNotes')}
          </button>

          <h4 className="lead-detail-heading">{t('outreach.leads.historyHeading')}</h4>
          {detail.history.length === 0 && <div className="unibox-empty">{t('outreach.leads.noHistory')}</div>}
          {detail.history.map((entry) => (
            <div key={entry.id} className="lead-history-entry">
              <span>{new Date(entry.changed_at).toLocaleString()}</span>
              <span>
                {entry.from_status || "-"} → <strong>{entry.to_status}</strong>
              </span>
              <span className="lead-history-source">
                {t(`outreach.leads.source.${entry.source}`, entry.source)}
              </span>
            </div>
          ))}

          <div className="lead-detail-danger">
            {confirmingErase ? (
              <>
                <p className="lead-detail-danger-text">{t('outreach.leads.eraseWarning')}</p>
                <button className="unibox-refresh lead-detail-erase" onClick={eraseLead} disabled={erasing}>
                  {erasing ? t('outreach.leads.erasing') : t('outreach.leads.eraseConfirm')}
                </button>
                <button className="unibox-refresh" onClick={() => setConfirmingErase(false)} disabled={erasing}>
                  {t('outreach.leads.eraseCancel')}
                </button>
              </>
            ) : (
              <button className="unibox-refresh lead-detail-erase" onClick={() => setConfirmingErase(true)}>
                <Trash2 size={13} /> {t('outreach.leads.erase')}
              </button>
            )}
          </div>
        </>
      )}
    </aside>
  );
}
